import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {FrontKeyboardEvent, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {InvalidPhoneNumberError} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {BoxedInputIcon} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputIcon';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InvalidEmailAddressError} from '../../../helpers/emailHelpers';
import {InlineEditingError, InlineEditingErrorMessage} from './inlineEditingErrorMessage';
import {InlineEditingInputDisplay} from './inlineEditingInputDisplay';

/*
 * Props.
 */

interface InlineEditingContactTextBoxProps {
  value: string;
  onEdit: HandlerOf<string>;
  onEditSave: HandlerOf<string>;
  onEditCancel: Handler;
  iconName: IconName;
  error: InlineEditingError;
}

/*
 * Style.
 */

const StyledInlineEditingInputDisplay = styled(InlineEditingInputDisplay)`
  margin: 0 1px;
`;

const StyledBoxedInputElement = styled(BoxedInputElement)`
  margin-left: 1px;
`;

/*
 * Component.
 */

export const InlineEditingContactTextBox: FC<InlineEditingContactTextBoxProps> = (props) => {
  const theme = useUserTheme();
  const {value, onEdit, onEditSave, onEditCancel, iconName, error} = props;

  const onKeyDown = (event: FrontKeyboardEvent) => {
    switch (event.key) {
      case Key.Enter:
        event.preventDefault();
        onEditSave(value);
        break;

      case Key.Escape:
        event.preventDefault();
        onEditCancel();
        break;

      default:
      // Do nothing.
    }
  };

  return (
    <StyledInlineEditingInputDisplay>
      <BoxedInput
        shouldAutoFocus
        noMargin
        hasError={Boolean(error)}
        errorMessage={<InlineEditingErrorMessage error={error} onError={onError} />}
      >
        {(childProps) => (
          <>
            <BoxedInputIcon {...childProps} iconName={iconName} color={theme.greys.shade70} />
            <StyledBoxedInputElement
              {...childProps}
              type={BoxedInputElementTypesEnum.TEXT}
              value={value}
              onChange={onEdit}
              onBlur={() => onEditSave(value)}
              onKeyDown={onKeyDown}
            />
          </>
        )}
      </BoxedInput>
    </StyledInlineEditingInputDisplay>
  );
};

function onError(error: InlineEditingError) {
  if (error instanceof InvalidEmailAddressError) {
    return <Trans id="contact-details-invalid-email-error-message">Please enter a valid email.</Trans>;
  }

  if (error instanceof InvalidPhoneNumberError) {
    return <Trans id="contact-details-invalid-phone-error-message">Please enter a valid phone number.</Trans>;
  }

  return undefined;
}
